/** /tour-operators/:id — tabbed edit page.
 *
 * Tabs: General (form mirroring `Tour_OperatorAdmin` minus inlines) +
 * Configuration / Integrations / Staff (placeholders for follow-up work).
 *
 * Preflight is drawn only for wizard-created tenants (`onboarding_managed`);
 * the rest have no checklist, so they get no onboarding surface at all.
 *
 * All navigation state is URL-driven (deep-linkable):
 *   /tour-operators/:id/:tab                              — top-level tab
 *   /tour-operators/:id/configuration/:sub                — subtab (assisted|raw|history)
 *   /tour-operators/:id/configuration/history/:changeId   — change-details dialog
 *   /tour-operators/:id/defaults/:section                 — scrolls to a section (tag-categories|trip-settings)
 *   /tour-operators/:id/pre-check-in[?edit=1]             — the pre-check-in form (record page + dock)
 *   /tour-operators/:id/integrations/:intgId[/:tab[/:view]] — record dialog (+ inner tab)
 *   /tour-operators/:id/sandbox                           — the sandbox environment (state, outbound,
 *                                                           quotas, reset; "Add sandbox" on production)
 *   /tour-operators/:id/readiness                         — a sandbox's readiness scorecard (the playbook's
 *                                                           done criteria over its activity)
 *   /tour-operators/:id/lifecycle                         — close / release / reopen requests, their
 *                                                           approval and the release / restore job
 *   /tour-operators/:id/offboarding                       — a closed tenant's offboarding checklist
 *   /tour-operators/:id/mcp                               — MCP kill switches per tool domain and the
 *                                                           auto-apply ops lock
 *   /tour-operators/:id/ai-vocabulary[?define=<word>]     — the company's words for our concepts (the AI's
 *                                                           knowledge tools only); `define` opens a new entry
 *
 * A sandbox (`environment: sandbox`) is a tenant of its own paired with its production operator: the
 * header carries the environment badge, the related strip links the pair, and the sandbox banners
 * (suspended, inbound paused, lane paused, production key) sit under it on every tab.
 */
import { Alert, Box, Button, CircularProgress, Tab, Tabs } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import * as topsApi from '@/api/tourOperators';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import type { HelpStoryId } from '@/help/registry.gen';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { cond, tourOperatorsHref } from '@/utils/gridLinks';
import { usePermissions } from '@/hooks/usePermissions';
import { UsersListPage } from '@/features/users/UsersListPage';

import { AIVocabularyTab } from './ai-vocabulary/AIVocabularyTab';
import { TenantRelated } from './components/TenantRelated';
import { IntegrationsTab } from './integrations/IntegrationsTab';
import { LifecycleBanner } from './lifecycle/LifecycleBanner';
import { LifecycleTab } from './lifecycle/LifecycleTab';
import { isClosedLifecycle } from './lifecycle/lifecycleWords';
import { OnboardingBanner } from './onboard/EasySetupBanner';
import { PreCheckInTab } from './precheck/PreCheckInTab';
import { ReadinessTab } from './readiness/ReadinessTab';
import { EnvironmentBadge } from './sandbox/EnvironmentBadge';
import { SandboxBanners } from './sandbox/SandboxBanners';
import { SandboxTab } from './sandbox/SandboxTab';
import { isSandbox } from './sandbox/useSandboxState';
import { AIEditTab } from './tabs/AIEditTab';
import { ConfigurationTab } from './tabs/ConfigurationTab';
import { DefaultsTab } from './tabs/DefaultsTab';
import { GeneralTab } from './tabs/GeneralTab';
import { McpTab } from './tabs/McpTab';
import { OffboardingTab } from './tabs/OffboardingTab';
import { PreflightTab } from './tabs/PreflightTab';
import { SignInSecurityTab } from './tabs/SignInSecurityTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'configuration', label: 'Configuration' },
  // Default tag categories + trip settings, each saved on its own.
  { id: 'defaults', label: 'Defaults' },
  // The pre-check-in form tours inherit: one draft, a live guest-form preview.
  { id: 'pre-check-in', label: 'Pre-check-in' },
  { id: 'integrations', label: 'Integrations' },
  // The sandbox environment (SBX D8): a sandbox's state and settings; a production operator's pair.
  { id: 'sandbox', label: 'Sandbox' },
  // A sandbox's readiness scorecard (client MCP plan §13.5): the integration playbook's done
  // criteria over its activity — what check_readiness answers its developer.
  { id: 'readiness', label: 'Readiness' },
  // Tenant retirement: the lifecycle (close / release / reopen requests, two-person approval, release gate, job).
  { id: 'lifecycle', label: 'Lifecycle' },
  // Tenant retirement: the offboarding checklist a closed tenant gets (what to undo outside the platform).
  { id: 'offboarding', label: 'Offboarding' },
  { id: 'preflight', label: 'Preflight' },
  { id: 'staff', label: 'Staff' },
  // MCP (client MCP plan §9 P0, Q26): the tenant's kill switches per tool domain and the ops lock
  // that keeps auto-apply off.
  { id: 'mcp', label: 'MCP' },
  // AI editing over MCP (backend metaplan A8): the tenant's MCP opt-in and MCP auto-apply.
  { id: 'ai-editing', label: 'AI editing' },
  // The company's words for our concepts, read only by the AI's knowledge tools (client MCP plan
  // §12.5): its entries, their history and change sets; support edits and reverts them.
  { id: 'ai-vocabulary', label: 'AI vocabulary' },
  // Dashboard two-step sign-in (dashboard password MFA plan §3.2): Optional or Mandatory.
  { id: 'sign-in-security', label: 'Sign-in security' },
  // Field-level history of the operator record + its logo/support contacts.
  // Config changes stay under Configuration → history (topconf).
  { id: 'history', label: 'History' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** The header's "?" follows the tab: a tab with a story of its own points there, the rest
 *  at the operator configuration story. */
const TAB_HELP: Partial<Record<TabId, HelpStoryId>> = {
  defaults: 'top-defaults',
  'pre-check-in': 'top-pre-check-in',
  sandbox: 'top-sandbox',
  readiness: 'integration-readiness',
  lifecycle: 'tenant-lifecycle',
  offboarding: 'tenant-lifecycle',
  'ai-vocabulary': 'top-ai-vocabulary',
};

export function TourOperatorEditPage() {
  const params = useParams<{ id: string; '*': string }>();
  const id = Number(params.id);
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canViewHistory = hasPerm('console_audit_view');

  const segments = (params['*'] ?? '').split('/').filter(Boolean).map(decodeURIComponent);

  const navigateTo = (tab: TabId, sub: string[] = []) =>
    navigate(`/tour-operators/${id}/${[tab as string, ...sub.map(encodeURIComponent)].join('/')}`);

  const {
    data: to,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tourOperators', 'detail', id],
    queryFn: () => topsApi.retrieve(id),
    enabled: Number.isFinite(id),
  });

  if (!Number.isFinite(id)) {
    return <Alert severity="warning">Invalid tour operator id.</Alert>;
  }
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !to) {
    return <Alert severity="error">Couldn’t load tour operator.</Alert>;
  }

  // A preflight checklist exists only for tenants the setup wizard created, so
  // for every other operator the tab is not drawn at all — nor the Setup entry
  // points beside it. Hidden, not disabled: there is nothing behind it to enable.
  const tabs = TABS.filter(
    (t) =>
      (t.id !== 'history' || canViewHistory) &&
      (t.id !== 'preflight' || to.onboarding_managed) &&
      (t.id !== 'pre-check-in' || hasPerm('console_top_view')) &&
      (t.id !== 'sandbox' || hasPerm('console_sandbox_view')) &&
      (t.id !== 'readiness' || (isSandbox(to) && hasPerm('console_integration_readiness_view'))) &&
      (t.id !== 'lifecycle' || hasPerm('console_tenant_lifecycle_view')) &&
      (t.id !== 'offboarding' || (hasPerm('console_preflight_view') && isClosedLifecycle(to.lifecycle))) &&
      (t.id !== 'mcp' || hasPerm('console_top_view')) &&
      (t.id !== 'ai-editing' || hasPerm('console_ai_changes_view')) &&
      (t.id !== 'ai-vocabulary' || hasPerm('console_ai_vocabulary_view')) &&
      (t.id !== 'sign-in-security' || hasPerm('console_top_view')),
  );
  // A deep link to a tab this operator does not have falls back to General, the
  // same fallback an unrecognised segment already gets — never an empty <Tabs>
  // value, which MUI renders as a tab strip with nothing selected.
  const activeTab: TabId = tabs.some((t) => t.id === segments[0])
    ? (segments[0] as TabId)
    : 'general';
  // Segments after the tab — the subtab / record-id path owned by the tab component.
  const subPath = tabs.some((t) => t.id === segments[0]) ? segments.slice(1) : [];

  return (
    <Box>
      <PageHeader
        helpStory={TAB_HELP[activeTab] ?? 'top-configuration'}
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={to.name}
        titleAdornment={<EnvironmentBadge environment={to.environment} paired={to.sandbox != null} />}
        subtitle={
          <DetailSubtitle
            parts={[
              <ValueActions
                key="code"
                actions={[
                  act.copy(to.code, 'Copy code'),
                  act.filter(tourOperatorsHref([cond('code', to.code)]), 'This operator in the grid'),
                ]}
              >
                Code: {to.code}
              </ValueActions>,
              <ValueActions key="id" actions={[act.copy(String(to.id), 'Copy id')]}>ID {to.id}</ValueActions>,
            ]}
          />
        }
        actions={
          to.onboarding_managed ? (
            <Button component={RouterLink} to={`/tour-operators/${to.id}/setup`}>
              Setup
            </Button>
          ) : undefined
        }
      />
      <TenantRelated to={to} />
      <SandboxBanners to={to} onSandboxTab={activeTab === 'sandbox'} />
      <LifecycleBanner to={to} onLifecycleTab={activeTab === 'lifecycle'} />
      {to.onboarding_managed && <OnboardingBanner topId={to.id} />}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigateTo(v as TabId)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane id="general" active={activeTab}>
        <GeneralTab to={to} onSaved={() => void refetch()} />
      </TabPane>
      <TabPane id="configuration" active={activeTab}>
        <ConfigurationTab
          to={to}
          onSaved={() => void refetch()}
          path={activeTab === 'configuration' ? subPath : []}
          onNavigate={(sub) => navigateTo('configuration', sub)}
        />
      </TabPane>
      <TabPane id="defaults" active={activeTab}>
        <DefaultsTab to={to} path={activeTab === 'defaults' ? subPath : []} />
      </TabPane>
      <TabPane id="pre-check-in" active={activeTab}>
        <PreCheckInTab to={to} />
      </TabPane>
      <TabPane id="integrations" active={activeTab}>
        <IntegrationsTab
          to={to}
          onSaved={() => void refetch()}
          path={activeTab === 'integrations' ? subPath : []}
          onNavigate={(sub) => navigateTo('integrations', sub)}
        />
      </TabPane>
      <TabPane id="sandbox" active={activeTab}>
        <SandboxTab to={to} />
      </TabPane>
      <TabPane id="readiness" active={activeTab}>
        <ReadinessTab to={to} />
      </TabPane>
      <TabPane id="lifecycle" active={activeTab}>
        <LifecycleTab to={to} />
      </TabPane>
      <TabPane id="offboarding" active={activeTab}>
        <OffboardingTab to={to} />
      </TabPane>
      {to.onboarding_managed && (
        <TabPane id="preflight" active={activeTab}>
          <PreflightTab
            to={to}
            onSaved={() => void refetch()}
            path={activeTab === 'preflight' ? subPath : []}
            onNavigate={(sub) => navigateTo('preflight', sub)}
          />
        </TabPane>
      )}
      <TabPane id="history" active={activeTab}>
        <HistoryPanel
          contentType="demo.tour_operator"
          objectPk={String(to.id)}
          onRestored={() => void refetch()}
        />
      </TabPane>
      {hasPerm('console_top_view') && (
        <TabPane id="mcp" active={activeTab}>
          {activeTab === 'mcp' && <McpTab to={to} />}
        </TabPane>
      )}
      {hasPerm('console_ai_changes_view') && (
        <TabPane id="ai-editing" active={activeTab}>
          {activeTab === 'ai-editing' && <AIEditTab topId={to.id} />}
        </TabPane>
      )}
      {hasPerm('console_ai_vocabulary_view') && (
        <TabPane id="ai-vocabulary" active={activeTab}>
          {activeTab === 'ai-vocabulary' && <AIVocabularyTab to={to} />}
        </TabPane>
      )}
      {hasPerm('console_top_view') && (
        <TabPane id="sign-in-security" active={activeTab}>
          {activeTab === 'sign-in-security' && <SignInSecurityTab topId={to.id} />}
        </TabPane>
      )}
      <TabPane id="staff" active={activeTab}>
        {/* Reuses the /users/staff grid, hardcoded to this tenant (Tenant
            column + filter hidden). */}
        <UsersListPage kind="staff" fixedTenantId={to.id} />
      </TabPane>

      <Box sx={{ mt: 4 }}>
        <RouterLink to="/tour-operators" style={{ color: '#0a1a3f' }}>
          ← Back to tour operators
        </RouterLink>
      </Box>
    </Box>
  );
}

function TabPane({ id, active, children }: { id: TabId; active: TabId; children: ReactNode }) {
  if (id !== active) return null;
  return <Box>{children}</Box>;
}
